import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Link as LinkIcon,
  DescriptionOutlined as FileIcon,
  AutoAwesomeOutlined as SparklesIcon,
  EqualizerOutlined as ChartIcon,
  FileDownloadOutlined as ExportIcon,
  FolderOpen as FolderIcon,
} from '@mui/icons-material';

interface UploadAudioModalProps {
  open: boolean;
  onClose: () => void;
}

// ── Google Drive Logo (Stylised Triangle) ──────────────────────────────────
const GoogleDriveLogo = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    {/* Left (Green) */}
    <path d="M8.002 15.467L4.002 22.396L0 15.467L7.785 1.98L11.784 8.911L7.999 15.467z" fill="#00A859" />
    {/* Right (Blue) */}
    <path d="M23.784 15.092H15.785L7.999 1.605H16.001L23.786 15.091z" fill="#0066DA" />
    {/* Bottom (Yellow) */}
    <path d="M4.435 22.396L8.433 15.467L24 15.467L20.002 22.396L4.435 22.396z" fill="#FFD000" />
  </svg>
);

// ── Overlapping Sine Wave Graphic for limits panel bottom ──────────────────
const WaveGraphic = () => (
  <svg width="100%" height="45" viewBox="0 0 200 45" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', marginTop: 'auto' }}>
    <path d="M0 25 C30 5, 50 40, 80 25 C110 5, 130 40, 160 25 C180 15, 190 25, 200 25" stroke="#10B981" strokeWidth="1.5" strokeOpacity="0.4" fill="none" />
    <path d="M0 30 C20 15, 40 35, 70 25 C100 10, 120 35, 150 25 C170 15, 190 30, 200 25" stroke="#10B981" strokeWidth="1" strokeOpacity="0.2" fill="none" />
  </svg>
);

const UploadAudioModal: React.FC<UploadAudioModalProps> = ({ open, onClose }) => {
  const [activeTab, setActiveTab] = useState<'files' | 'folder' | 'drive'>('files');
  const [driveUrl, setDriveUrl] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const handleBrowseClick = () => {
    if (activeTab === 'folder') {
      if (folderInputRef.current) {
        folderInputRef.current.click();
      }
    } else {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      // TODO: process selected files/folders and pass them up
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '780px',
          height: '460px',
          background: '#0C0C0C',
          border: '1px solid #1A1A1A',
          borderRadius: '16px',
          boxShadow: '0 24px 72px rgba(0,0,0,0.8)',
          overflow: 'hidden',
          p: 0,
          m: 0,
        },
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          color: '#666',
          zIndex: 10,
          '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.05)' },
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>

      <DialogContent sx={{ display: 'flex', p: 0, height: '100%' }}>
        {/* ── LEFT CONTAINER (Tabs and Input Forms) ────────────────────────── */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 4.5, pr: 3.5, boxSizing: 'border-box' }}>
          {/* Header */}
          <Box sx={{ mb: 4.5 }}>
            <Typography
              sx={{
                fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
                fontSize: '18px',
                fontWeight: 700,
                color: '#fff',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                mb: 0.5,
              }}
            >
              Upload Acoustic Data
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
                fontSize: '11px',
                color: '#666',
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              Import wildlife audio recordings for biodiversity and acoustic analysis.
            </Typography>
          </Box>

          {/* Custom Tabs Buttons */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 4 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloudUploadIcon sx={{ fontSize: 13 }} />}
              onClick={() => setActiveTab('files')}
              sx={{
                fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
                fontSize: '10.5px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                borderRadius: '6px',
                height: '34px',
                px: 2,
                color: activeTab === 'files' ? '#10B981' : '#666',
                borderColor: activeTab === 'files' ? 'rgba(16, 185, 129, 0.4)' : 'rgba(255, 255, 255, 0.08)',
                background: activeTab === 'files' ? 'rgba(16, 185, 129, 0.04)' : 'transparent',
                '&:hover': {
                  borderColor: activeTab === 'files' ? '#10B981' : 'rgba(255, 255, 255, 0.15)',
                  background: activeTab === 'files' ? 'rgba(16, 185, 129, 0.08)' : 'rgba(255,255,255,0.02)',
                },
              }}
            >
              Upload Files
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<FolderIcon sx={{ fontSize: 13 }} />}
              onClick={() => setActiveTab('folder')}
              sx={{
                fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
                fontSize: '10.5px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                borderRadius: '6px',
                height: '34px',
                px: 2,
                color: activeTab === 'folder' ? '#10B981' : '#666',
                borderColor: activeTab === 'folder' ? 'rgba(16, 185, 129, 0.4)' : 'rgba(255, 255, 255, 0.08)',
                background: activeTab === 'folder' ? 'rgba(16, 185, 129, 0.04)' : 'transparent',
                '&:hover': {
                  borderColor: activeTab === 'folder' ? '#10B981' : 'rgba(255, 255, 255, 0.15)',
                  background: activeTab === 'folder' ? 'rgba(16, 185, 129, 0.08)' : 'rgba(255,255,255,0.02)',
                },
              }}
            >
              Upload Folder
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<GoogleDriveLogo style={{ fontSize: 13 }} />}
              onClick={() => setActiveTab('drive')}
              sx={{
                fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
                fontSize: '10.5px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                borderRadius: '6px',
                height: '34px',
                px: 2,
                color: activeTab === 'drive' ? '#10B981' : '#666',
                borderColor: activeTab === 'drive' ? 'rgba(16, 185, 129, 0.4)' : 'rgba(255, 255, 255, 0.08)',
                background: activeTab === 'drive' ? 'rgba(16, 185, 129, 0.04)' : 'transparent',
                '&:hover': {
                  borderColor: activeTab === 'drive' ? '#10B981' : 'rgba(255, 255, 255, 0.15)',
                  background: activeTab === 'drive' ? 'rgba(16, 185, 129, 0.08)' : 'rgba(255,255,255,0.02)',
                },
              }}
            >
              Google Drive
            </Button>
          </Box>

          {/* Hidden File Input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            multiple
            accept=".wav,.mp3,.flac"
            style={{ display: 'none' }}
          />

          {/* Hidden Folder Input */}
          <input
            type="file"
            ref={folderInputRef}
            onChange={handleFileChange}
            multiple
            {...({
              webkitdirectory: 'true',
              directory: '',
            } as React.InputHTMLAttributes<HTMLInputElement>)}
            style={{ display: 'none' }}
          />

          {/* Tab Panels */}
          {activeTab === 'files' || activeTab === 'folder' ? (
            /* Drag & Drop Files View */
            <Box
              onClick={handleBrowseClick}
              sx={{
                flex: 1,
                border: '1.5px dashed rgba(255,255,255,0.06)',
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                background: 'rgba(255,255,255,0.01)',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'rgba(16, 185, 129, 0.3)',
                  background: 'rgba(16, 185, 129, 0.01)',
                },
              }}
            >
              {activeTab === 'folder' ? (
                <FolderIcon sx={{ fontSize: 32, color: '#10B981', mb: 2, opacity: 0.8 }} />
              ) : (
                <CloudUploadIcon sx={{ fontSize: 32, color: '#10B981', mb: 2, opacity: 0.8 }} />
              )}
              <Typography
                sx={{
                  fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
                  fontSize: '14px',
                  color: '#aaa',
                  mb: 1.5,
                  fontWeight: 500,
                  textAlign: 'center',
                }}
              >
                Drag & drop audio {activeTab === 'folder' ? 'folders' : 'files'} <br />
                <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>or </Typography>
                <Typography component="span" sx={{ color: '#10B981', fontSize: '14px', fontWeight: 700, textDecoration: 'underline' }}>browse</Typography>
                <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}> from your device</Typography>
              </Typography>

              {/* Supported formats badges */}
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5, mt: 0.5 }}>
                {['WAV', 'MP3', 'FLAC'].map((format) => (
                  <Box
                    key={format}
                    sx={{
                      fontSize: '9px',
                      fontWeight: 700,
                      color: '#10B981',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      background: 'rgba(16, 185, 129, 0.05)',
                      borderRadius: '4px',
                      px: 0.8,
                      py: 0.2,
                    }}
                  >
                    {format}
                  </Box>
                ))}
              </Box>

              <Typography sx={{ fontSize: '9px', color: '#444', fontWeight: 600, letterSpacing: '0.5px' }}>
                Maximum 10 files in exploration mode.
              </Typography>
            </Box>
          ) : (
            /* Google Drive Input View */
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography
                sx={{
                  fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
                  fontSize: '13px',
                  fontWeight: 700,
                  color: '#fff',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  mb: 0.5,
                }}
              >
                Import from Google Drive
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
                  fontSize: '11px',
                  color: '#555',
                  mb: 2.5,
                }}
              >
                Google drive folder or file link
              </Typography>

              <TextField
                fullWidth
                variant="outlined"
                placeholder="Paste google drive folder or file URL here"
                value={driveUrl}
                onChange={(e) => setDriveUrl(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon sx={{ color: '#444', fontSize: 16 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2.5,
                  '& .MuiOutlinedInput-root': {
                    height: '42px',
                    backgroundColor: 'rgba(255,255,255,0.01)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: '#fff',
                    fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
                    '& fieldset': { border: 'none' },
                    '&:hover': {
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                    },
                    '&.Mui-focused': {
                      border: '1px solid #10B981',
                      boxShadow: '0 0 10px rgba(16, 185, 129, 0.15)',
                    },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#444',
                    opacity: 1,
                  },
                }}
              />

              <Typography
                sx={{
                  fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
                  fontSize: '10.5px',
                  color: '#555',
                  lineHeight: 1.45,
                  maxWidth: '380px',
                }}
              >
                Make sure the link shared is public. with enabled <br />
                anyone can view share option.
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── RIGHT CONTAINER (Exploration Mode Limits Sidebar) ─────────────── */}
        <Box
          sx={{
            width: '260px',
            background: 'linear-gradient(180deg, #111 0%, #0D0D0D 100%)',
            borderLeft: '1px solid #1A1A1A',
            p: 4.5,
            pt: 5,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Hexagonal Exclamation Icon */}
          <Box
            sx={{
              width: 48,
              height: 48,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              mb: 3,
            }}
          >
            {/* Hexagon outline SVG */}
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ position: 'absolute', inset: 0 }}
            >
              <path
                d="M12 2L20 7V17L12 22L4 17V7L12 2Z"
                stroke="#10B981"
                strokeWidth="1.5"
                fill="rgba(16, 185, 129, 0.05)"
              />
            </svg>
            <Typography sx={{ color: '#10B981', fontSize: '18px', fontWeight: 900 }}>!</Typography>
          </Box>

          <Typography
            sx={{
              fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
              fontSize: '11px',
              fontWeight: 700,
              color: '#10B981',
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              mb: 3.5,
              textAlign: 'center',
            }}
          >
            Exploration Mode <br /> Limits
          </Typography>

          {/* Limits bullet items */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', mb: 4.5 }}>
            {[
              { label: 'Max 10 audio uploads', icon: FileIcon },
              { label: 'AI species detection enabled', icon: SparklesIcon },
              { label: 'Basic spectrogram preview', icon: ChartIcon },
              { label: 'Limited export access', icon: ExportIcon },
            ].map((limit, idx) => {
              const Icon = limit.icon;
              return (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Icon sx={{ fontSize: 13, color: '#10B981', opacity: 0.8 }} />
                  <Typography
                    sx={{
                      fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
                      fontSize: '11px',
                      color: '#999',
                      fontWeight: 500,
                    }}
                  >
                    {limit.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* Overlay Sine wave at bottom */}
          <WaveGraphic />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default UploadAudioModal;
